import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoIcon } from '../components/Logo';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in URL');
        window.history.replaceState({}, document.title, '/app');
        navigate('/app', { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      // DO NOT clear the hash yet — checkAuth needs to see it to skip its auth check
      // The hasProcessed ref prevents double processing

      // Retry logic — session exchange can fail if page load is slow
      const maxRetries = 3;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            console.log(`[AuthCallback] Retry attempt ${attempt + 1}/${maxRetries}`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }

          const userData = await exchangeSession(sessionId);
          
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const storedToken = localStorage.getItem('wwi_session_token');
          console.log('[AuthCallback] Token stored in localStorage:', !!storedToken);
          
          // Clear hash AFTER successful exchange
          window.history.replaceState({}, document.title, '/app');
          navigate('/app', { replace: true, state: { user: userData, justSignedIn: true } });
          return; // Success — exit
        } catch (error) {
          console.error(`[AuthCallback] Attempt ${attempt + 1} failed:`, error);
          if (attempt === maxRetries - 1) {
            console.error('[AuthCallback] All attempts failed');
            // Clear hash on final failure too
            window.history.replaceState({}, document.title, '/app');
            navigate('/app', { replace: true });
          }
        }
      }
    };

    processCallback();
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-pulse mb-4">
          <LogoIcon size={64} />
        </div>
        <p className="text-[#52525B]">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
